import React, { useEffect } from 'react'
import { styled } from '@mui/material/styles'
import { TextField as MuiTextField } from '@mui/material'
import PropTypes from 'prop-types'
import * as ResizeOptions from './TextAreaResizeOptions'
import ThemeConfig from './../../theme/ThemeConfig'

const PREFIX = 'TextField'

const classes = {
  root: `${PREFIX}-root`,
  cssLabel: `${PREFIX}-cssLabel`,
  cssHelperText: `${PREFIX}-cssHelperText`,
  cssHelperTextError: `${PREFIX}-cssHelperTextError`,
  inputStyleText: `${PREFIX}-inputStyleText`,
  inputStyleTextArea: `${PREFIX}-inputStyleTextArea`,
  inputStyleWithResizeEnabledBoth: `${PREFIX}-inputStyleWithResizeEnabledBoth`,
  inputStyleWithResizeEnabledHorizontal: `${PREFIX}-inputStyleWithResizeEnabledHorizontal`,
  inputStyleWithResizeEnabledVertical: `${PREFIX}-inputStyleWithResizeEnabledVertical`,
  adjustLabelPosition: `${PREFIX}-adjustLabelPosition`,
  inputBaseRoot: `${PREFIX}-inputBaseRoot`,
}

const labelTranslate = 'translate(14px, 12px) scale(1)'
const inputHeight = '40px'

const StyledMuiTextField = styled(MuiTextField)(({ theme }) => ({
  [`& .${classes.root}`]: {
    color: ThemeConfig.palette.secondary.darkest,
    borderColor: ThemeConfig.palette.error.main,
    '& label.Mui-focused': {
      color: ThemeConfig.palette.secondary.main,
    },
    '& .MuiInputBase-input': {
      color: ThemeConfig.palette.secondary.darkest,
      borderColor: ThemeConfig.palette.secondary.darkest,
    },
    '& .MuiInputBase-root': {
      color: ThemeConfig.palette.secondary.darkest,
      borderColor: ThemeConfig.palette.secondary.darkest,
      fontSize: ThemeConfig.typography.body1.fontSize,
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '3px',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        color: ThemeConfig.palette.secondary.darkest,
        borderColor: ThemeConfig.palette.secondary.darkest,
      },
      '&.Mui-focused fieldset': {
        borderColor: ThemeConfig.palette.secondary.darkest,
        color: ThemeConfig.palette.secondary.darkest,
      },
    },
    '& .MuiFormLabel-root.Mui-disabled': {
      color: ThemeConfig.palette.secondary.main,
      borderColor: ThemeConfig.palette.secondary.darkest,
    },
    '& .MuiInputBase-root.Mui-disabled': {
      color: ThemeConfig.palette.secondary.main,
      backgroundColor: ThemeConfig.palette.inputDisabled.backgroundColor,
      borderColor: ThemeConfig.palette.secondary.darkest,
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: ThemeConfig.palette.secondary.main,
      borderColor: ThemeConfig.palette.secondary.darkest,
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      color: ThemeConfig.palette.secondary.main,
      borderColor: ThemeConfig.palette.secondary.main,
    },
    '& .MuiFormHelperText-root.Mui-disabled': {
      color: ThemeConfig.palette.secondary.main,
      borderColor: ThemeConfig.palette.secondary.darkest,
    },
    '& .MuiOutlinedInput-root.Mui-error': {
      '& fieldset': {
        borderColor: ThemeConfig.palette.error.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: ThemeConfig.palette.error.main,
      },
    },
    '& label.Mui-error': {
      color: ThemeConfig.palette.error.main,
    },
    '& .MuiFormHelperText-root.Mui-error': {
      color: ThemeConfig.palette.error.main,
    },
  },

  [`& .${classes.cssLabel}`]: {
    color: ThemeConfig.palette.secondary.main,
    fontSize: ThemeConfig.typography.inputText.fontSize,
  },

  [`& .${classes.cssHelperText}`]: {
    fontStyle: 'italic',
    color: ThemeConfig.palette.secondary.main,
  },

  [`& .${classes.cssHelperTextError}`]: {
    color: ThemeConfig.palette.error.main,
  },

  [`& .${classes.inputStyleText}`]: {
    textOverflow: 'ellipsis',
    height: inputHeight,
  },

  [`& .${classes.inputStyleTextArea}`]: {
    textOverflow: 'ellipsis',
  },

  [`& .${classes.inputStyleWithResizeEnabledBoth}`]: {
    textOverflow: 'ellipsis',
    resize: 'both',
  },

  [`& .${classes.inputStyleWithResizeEnabledHorizontal}`]: {
    textOverflow: 'ellipsis',
    resize: 'horizontal',
  },

  [`& .${classes.inputStyleWithResizeEnabledVertical}`]: {
    textOverflow: 'ellipsis',
    resize: 'vertical',
  },

  [`& .${classes.adjustLabelPosition}`]: {
    transform: labelTranslate,
  },

  [`& .${classes.inputBaseRoot}`]: {
    height: inputHeight,
  },
}))

const TextField = props => {
  const {
    hintText,
    label,
    disabled,
    error,
    value,
    errorMessage,
    multiline,
    rows,
    fullWidth,
    onChange,
    id,
    resize,
    defaultValue,
    required,
    clearContent,
    ...others
  } = props
  let helperTextMessage = ''
  let cssHelperTextClass = classes.cssHelperText
  let inputStyleSelected = ''
  switch (resize) {
    case ResizeOptions.RESIZE_DIRECTION_TYPES.BOTH:
      inputStyleSelected = classes.inputStyleWithResizeEnabledBoth
      break
    case ResizeOptions.RESIZE_DIRECTION_TYPES.HORIZONTAL:
      inputStyleSelected = classes.inputStyleWithResizeEnabledHorizontal
      break
    case ResizeOptions.RESIZE_DIRECTION_TYPES.VERTICAL:
      inputStyleSelected = classes.inputStyleWithResizeEnabledVertical
      break
    default:
      if (multiline === true) {
        inputStyleSelected = classes.inputStyleTextArea
      } else {
        inputStyleSelected = classes.inputStyleText
      }
  }

  let inputBaseRootSelected = ''
  if (multiline === undefined || multiline === false) {
    inputBaseRootSelected = classes.inputBaseRoot
  }
  if (error === undefined || error === false) {
    helperTextMessage = hintText
  } else if (error === true) {
    helperTextMessage = errorMessage
    cssHelperTextClass = classes.cssHelperTextError
  }

  const [currentValue, setCurrentValue] = React.useState(value ?? '')

  const handleChange = e => {
    setCurrentValue(e.target.value)
    if (onChange) {
      onChange(e)
    }
  }

  const handleKeyPress = event => {
    if (props.onKeyPress) {
      props.onKeyPress(event)
    }
  }

  useEffect(() => {
    if (value !== undefined && value !== currentValue) {
      setCurrentValue(value)
    }
  }, [value, currentValue])

  let inputLabelCssClasses = classes.cssLabel
  inputLabelCssClasses += ' ' + classes.adjustLabelPosition

  return (
    <StyledMuiTextField
      label={label}
      variant="outlined"
      id={id}
      required={required}
      helperText={helperTextMessage}
      disabled={disabled}
      error={error}
      value={currentValue}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      className={classes.root}
      multiline={multiline}
      rows={rows}
      fullWidth={fullWidth}
      defaultValue={defaultValue}
      FormHelperTextProps={{
        classes: {
          root: cssHelperTextClass,
        },
      }}
      InputProps={{
        classes: {
          inputMultiline: inputStyleSelected,
          input: inputStyleSelected,
          root: inputBaseRootSelected,
        },
      }}
      {...others}
    />
  )
}

TextField.propTypes = {
  hintText: PropTypes.string,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  fullWidth: PropTypes.bool,
  onClick: PropTypes.func,
  onKeyPress: PropTypes.func,
  id: PropTypes.string.isRequired,
  resize: PropTypes.string,
  defaultValue: PropTypes.string,
  required: PropTypes.bool,
}

export default TextField
